/** Meine Artikel (Mieterportal 2.0): Foto-Raster oder Liste, Filter (auch Lagerhüter, ohne Foto, Packliste), Auswahl-Modus mit Preis senken / Abgeholt / Etiketten. */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button, Dialog, EmptyState, Icon, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatDatum, tagVon } from "@shared/zeit";
import { tageZwischen } from "@shared/perioden";
import { fotoUrl, useMeineArtikel, usePreisSenken, type ArtikelStatus, type ArtikelZeile } from "./artikel-api";
import { useStatusMehrere } from "./cockpit-api";
import { useUmziehen } from "./portal-c-api";
import { useErfassenInfo } from "./artikel-api";
import { useTx } from "../i18n";

export const STATUS_BADGE: Record<ArtikelStatus, { status: "shelf" | "sold" | "returnbox" | "neutral" | "wait" | "attention"; text: string }> = {
  im_regal: { status: "shelf", text: "Im Regal" },
  im_warenkorb: { status: "wait", text: "An der Kasse" },
  verkauft: { status: "sold", text: "Verkauft" },
  rueckgabe_kiste: { status: "returnbox", text: "Rückgabe-Kiste" },
  abgeholt: { status: "neutral", text: "Abgeholt" },
  schwund: { status: "attention", text: "Schwund" },
  entfernt: { status: "neutral", text: "Entfernt" },
};

export function artikelMeta(a: ArtikelZeile): string {
  const heute = tagVon();
  if (a.status === "verkauft") return `${a.code} · verkauft ${formatDatum(a.statusAt)}`;
  if (a.status === "im_regal") {
    const tage = tageZwischen(a.erstelltAt.slice(0, 10), heute);
    return tage >= 30 ? `${a.code} · liegt seit ${tage} Tagen` : a.code;
  }
  return a.code;
}

type Filter = "alle" | ArtikelStatus | "lagerhueter" | "ohne_foto" | "packliste" | "druckliste";
const SONDER: Record<string, { nur: "lagerhueter" | "ohne_foto" | "packliste" | "druckliste"; text: string }> = {
  lagerhueter: { nur: "lagerhueter", text: "Liegt lange" },
  ohne_foto: { nur: "ohne_foto", text: "Ohne Foto" },
  packliste: { nur: "packliste", text: "Einräumen" },
  druckliste: { nur: "druckliste", text: "Etikett fehlt" },
};

export function MeineArtikel() {
  const tx = useTx();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") as Filter) || "alle";
  const [suche, setSuche] = useState("");
  const [ansicht, setAnsicht] = useState<"raster" | "liste">(() => (localStorage.getItem("mp-ansicht") as "raster" | "liste") || "raster");
  const [auswahlModus, setAuswahlModus] = useState(false);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [senken, setSenken] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const sonder = SONDER[filter];
  const alle = useMeineArtikel(undefined, suche || undefined, sonder?.nur);
  const statusMehrere = useStatusMehrere();
  const umziehen = useUmziehen();
  const info = useErfassenInfo();
  const regale = info.data?.regale ?? [];
  const navigate = useNavigate();
  const liste = alle.data?.artikel ?? [];
  useEffect(() => localStorage.setItem("mp-ansicht", ansicht), [ansicht]);
  const zaehler = useMemo(() => {
    const z: Partial<Record<ArtikelStatus, number>> = {};
    for (const a of liste) z[a.status] = (z[a.status] ?? 0) + 1;
    return z;
  }, [liste]);
  const gefiltert = sonder || filter === "alle" ? liste : liste.filter((a) => a.status === filter);
  const waehlbar = (a: ArtikelZeile) => a.status === "im_regal";
  const toggle = (id: string) => setAuswahl((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const setFilter = (f: Filter) => setParams(f === "alle" ? {} : { filter: f });
  const oeffnen = (a: ArtikelZeile) => (auswahlModus ? (waehlbar(a) ? toggle(a.id) : undefined) : navigate(`/mieter/artikel/${a.id}`));

  return (
    <div className="sc-col" style={{ gap: "var(--space-3)" }}>
      <div className="sc-spread">
        <h1 className="title">{tx("Meine Artikel")}</h1>
        <span className="sc-row">
          <Button variant="quiet" icon={ansicht === "raster" ? "list" : "layout-dashboard"} aria-label={ansicht === "raster" ? "Als Liste" : "Als Raster"} onClick={() => setAnsicht(ansicht === "raster" ? "liste" : "raster")} />
          <Button variant={auswahlModus ? "primary" : "secondary"} icon="check" onClick={() => { setAuswahlModus(!auswahlModus); setAuswahl(new Set()); }}>{auswahlModus ? "Fertig" : "Auswählen"}</Button>
        </span>
      </div>
      <TextField label="Suchen" icon="search" type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Artikel oder Code suchen" />
      <div className="sc-toolbar" style={{ overflowX: "auto", flexWrap: "nowrap", paddingBottom: 2 }}>
        {(
          [
            ["alle", `Alle ${sonder ? "" : liste.length}`.trim()],
            ["im_regal", `Im Regal${zaehler.im_regal ? ` ${zaehler.im_regal}` : ""}`],
            ["verkauft", `Verkauft${zaehler.verkauft ? ` ${zaehler.verkauft}` : ""}`],
            ["lagerhueter", "Liegt lange"],
            ["ohne_foto", "Ohne Foto"],
            ["druckliste", "Etikett fehlt"],
            ["rueckgabe_kiste", "Rückgabe-Kiste"],
            ["abgeholt", "Abgeholt"],
          ] as [Filter, string][]
        ).map(([k, t]) => (
          <button key={k} type="button" className="sc-pill" aria-pressed={filter === k} onClick={() => setFilter(k)} style={{ flex: "none" }}>{t}</button>
        ))}
      </div>
      {meldung ? <Notice tone="success">{meldung}</Notice> : null}
      {auswahlModus ? <p className="sc-muted sc-small">Artikel im Regal antippen, um sie auszuwählen. <button type="button" className="rg-btn rg-btn--quiet" onClick={() => setAuswahl(new Set(gefiltert.filter(waehlbar).map((a) => a.id)))}>Alle sichtbaren</button></p> : null}
      <Seitenzustand query={alle} lines={5} thumb istLeer={(d) => d.artikel.length === 0 && !suche && !sonder} leer={<EmptyState illustration="empty-shelf" title={tx("Noch keine Artikel")} action={<Link to="/mieter/erfassen" className="rg-btn rg-btn--primary">Ersten Artikel erfassen</Link>}>Fotografiere deine Sachen, gib einen Preis ein, drucke Etiketten – los geht’s.</EmptyState>}>
        {() =>
          gefiltert.length === 0 ? (
            <EmptyState compact illustration="all-done" title={sonder ? `Nichts bei „${sonder.text}“` : "Nichts in dieser Ansicht"}>{filter === "lagerhueter" ? "Alles verkauft sich – weiter so." : filter === "ohne_foto" ? "Alle Artikel haben ein Foto." : filter === "druckliste" ? "Alle Etiketten sind gedruckt." : "Probier einen anderen Filter."}</EmptyState>
          ) : ansicht === "raster" ? (
            <div className="mp-raster">
              {gefiltert.map((a) => {
                const b = STATUS_BADGE[a.status];
                const on = auswahl.has(a.id);
                return (
                  <button key={a.id} type="button" className={`mp-kachel-artikel${on ? " is-on" : ""}`} onClick={() => oeffnen(a)} aria-pressed={auswahlModus ? on : undefined} aria-label={`${a.bezeichnung}, ${formatEuro(a.preisCent)}, ${b.text}`}>
                    {a.fotoKey ? <img className="mp-kachel-artikel__bild" src={fotoUrl(a.fotoKey)} alt="" loading="lazy" /> : <span className="mp-kachel-artikel__bild"><Icon name="image-off" size={28} /></span>}
                    <span className="mp-kachel-artikel__preis">{formatEuro(a.preisCent)}</span>
                    {auswahlModus && waehlbar(a) ? <span className="mp-kachel-artikel__check">{on ? <Icon name="check" size={16} /> : null}</span> : null}
                    {a.status !== "im_regal" ? <span className="mp-kachel-artikel__badge"><StatusBadge status={b.status}>{b.text}</StatusBadge></span> : null}
                    <span className="mp-kachel-artikel__text">
                      <span className="mp-kachel-artikel__titel">{a.bezeichnung}</span>
                      <span className="sc-muted sc-small">{artikelMeta(a).replace(`${a.code} · `, "").replace(a.code, "") || a.code}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div>
              {gefiltert.map((a) => {
                const b = STATUS_BADGE[a.status];
                return <ItemRow key={a.id} title={a.bezeichnung} photo={fotoUrl(a.fotoKey)} meta={artikelMeta(a)} status={<StatusBadge status={b.status}>{b.text}</StatusBadge>} price={a.preisCent} selected={auswahl.has(a.id)} onClick={() => oeffnen(a)} />;
              })}
            </div>
          )
        }
      </Seitenzustand>
      {auswahlModus && auswahl.size ? (
        <div className="mp-foot">
          <div className="sc-toolbar">
            <Button variant="primary" icon="percent" onClick={() => setSenken(true)}>Preis senken ({auswahl.size})</Button>
            <Link to={`/mieter/etiketten?ids=${[...auswahl].join(",")}`} className="rg-btn rg-btn--secondary">Etiketten</Link>
            {regale.length > 1 ? regale.map((r) => <Button key={r.regalId} variant="secondary" icon="library" loading={umziehen.isPending} onClick={() => umziehen.mutate({ artikelIds: [...auswahl], regalId: r.regalId }, { onSuccess: (x) => { setMeldung(`${x.umgezogen} Artikel nach Regal ${r.nummer} umgezogen – neue Codes, bitte Etiketten drucken.`); setAuswahl(new Set()); }, onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Umziehen hat nicht geklappt.") })}>Nach {r.nummer}</Button>) : null}
            <Button variant="secondary" icon="package" loading={statusMehrere.isPending} onClick={() => statusMehrere.mutate({ artikelIds: [...auswahl], status: "abgeholt" }, { onSuccess: (r) => { setMeldung(`${r.geaendert} Artikel als abgeholt markiert.`); setAuswahl(new Set()); }, onError: (e) => setMeldung(e instanceof ApiFehler ? e.message : "Das hat nicht geklappt.") })}>Abgeholt</Button>
          </div>
        </div>
      ) : null}
      {senken ? <PreisSenkenDialog ids={[...auswahl]} onClose={() => setSenken(false)} onFertig={() => { setSenken(false); setAuswahl(new Set()); setMeldung("Preise gesenkt – bitte neue Etiketten drucken."); }} /> : null}
    </div>
  );
}

export function PreisSenkenDialog({ ids, onClose, onFertig }: { ids: string[]; onClose: () => void; onFertig: () => void }) {
  const tx = useTx();
  const senken = usePreisSenken();
  const [art, setArt] = useState<"prozent" | "preis">("prozent");
  const [wert, setWert] = useState("20");
  const [fehler, setFehler] = useState<string | null>(null);
  const cent = parseEuro(wert);
  const prozent = Math.max(1, Math.min(90, Number(wert.replace(",", ".")) || 0));
  async function los() {
    setFehler(null);
    try {
      await senken.mutateAsync({ artikelIds: ids, art, wert: art === "prozent" ? prozent : (cent ?? 0) });
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt.");
    }
  }
  return (
    <Dialog open title={`Preis senken (${ids.length})`} size="sm" onClose={onClose} actions={<><Button variant="quiet" onClick={onClose}>{tx("Abbrechen")}</Button><Button variant="primary" icon="percent" loading={senken.isPending} disabled={art === "preis" && !cent} onClick={() => void los()}>Senken</Button></>}>
      <div className="app-form">
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <div className="sc-toolbar" role="radiogroup" aria-label="Art">
          <button type="button" className="sc-pill" aria-pressed={art === "prozent"} onClick={() => { setArt("prozent"); setWert("20"); }}>Um Prozent</button>
          <button type="button" className="sc-pill" aria-pressed={art === "preis"} onClick={() => { setArt("preis"); setWert(""); }}>Auf einen Preis</button>
        </div>
        {art === "prozent" ? (
          <>
            <TextField label="Senken um" value={wert} onChange={(e) => setWert(e.target.value)} inputMode="numeric" suffix="%" autoFocus />
            <div className="sc-toolbar">{[10, 20, 30, 50].map((p) => <button key={p} type="button" className="sc-pill" aria-pressed={prozent === p} onClick={() => setWert(String(p))}>{p} %</button>)}</div>
          </>
        ) : (
          <TextField label="Neuer Preis" value={wert} onChange={(e) => setWert(e.target.value)} inputMode="decimal" suffix="€" placeholder="0,00" autoFocus hint={cent ? `Alle gewählten Artikel kosten dann ${formatEuro(cent)} (nur wenn das günstiger ist)` : undefined} />
        )}
        <Notice tone="info">Nur Artikel im Regal werden geändert. Danach brauchen sie neue Etiketten – die alten zeigen den alten Preis.</Notice>
      </div>
    </Dialog>
  );
}
